import {ChangeDetectionStrategy, Component, inject, input, OnInit, output, signal} from '@angular/core';
import {
  customerFormFields,
  customerResourceType,
  isCustomer
} from "../../configs/customer-modal-config";
import {FormGroup, FormsModule, ReactiveFormsModule} from "@angular/forms";
import {AttachData, CustomerData, CustomerDialogData} from "../../types/customer-modal.types";
import {defaultRecordsDataRequestQueryParams} from "../../../../../models/default-records-data-request-query-params";
import {RecordsDataRequestQueryParams} from "../../../../../types/records-table.types";
import {MatTabChangeEvent, MatTabsModule} from "@angular/material/tabs";
import {CustomerModalService} from "../../services/customer-modal.service";
import {CloseModalReason} from "../../../../../types/modal.types";
import {MatFormField, MatInput, MatLabel} from "@angular/material/input";
import {MatListOption, MatSelectionList} from "@angular/material/list";
import {MatButton} from "@angular/material/button";

@Component({
  selector: 'app-new-customer-modal',
  standalone: true,
  imports: [
    MatTabsModule,
    ReactiveFormsModule,
    MatFormField,
    MatLabel,
    MatInput,
    FormsModule,
    MatSelectionList,
    MatListOption,
    MatButton
  ],
  templateUrl: './new-customer-modal.component.html',
  styleUrl: './new-customer-modal.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class NewCustomerModalComponent implements OnInit {
  customerFormGroup = input.required<FormGroup>();
  customerDialogData = input.required<CustomerDialogData>();
  attachCustomer = output<CustomerData>();
  closeDialog = output<CloseModalReason>();
  updateLoading = output<boolean>();

  private customerModalService: CustomerModalService = inject(CustomerModalService);

  public readonly customerFormFields = customerFormFields;
  public customers = signal<Array<CustomerData>>([]);
  public searchValue = signal<string>('');
  public selectedCustomer = signal<Array<CustomerData>>([]);
  public hasCustomers = signal<boolean>(false);
  private selectedTab = signal<number>(0);

  public async ngOnInit(): Promise<void> {
    await this.getCustomers();
  }

  public handleTabChange({ index }: MatTabChangeEvent): void {
    this.selectedTab.set(index);
  }

  public getCurrentCustomerTab(): string {
    const count = this.customers().length || '-';
    return `Выбрать существующего (${count})`
  }

  public async searchChange(): Promise<void> {
    await this.getCustomers();
  }

  public async handleAttach(): Promise<void> {
    this.selectedTab() ?
      await this.attach(this.selectedCustomer().at(0)) :
      await this.createCustomerData();
  }

  public getTextButton(): string {
    return this.selectedTab() ? 'Прикрепить' : 'Создать';
  }

  private async getCustomers(): Promise<void> {
    this.updateLoading.emit(true);
    const params: RecordsDataRequestQueryParams = {
      ...defaultRecordsDataRequestQueryParams,
      size: undefined,
      search: this.searchValue()
    };

    const data = await this.customerModalService.fetchCustomersData(customerResourceType.path, params)
      .catch(() => null)
      .finally(() => this.updateLoading.emit(false));
    this.customers.set(data ? data.content.filter(isCustomer) : []);
    this.hasCustomers.set(!!this.customers().length);
  }

  private async createCustomerData(): Promise<void> {
    await this.customerModalService.createCustomer(this.customerFormGroup().value, this.customerDialogData().feedbackId)
      .then((data) => this.attachCustomer.emit(data));
  }

  private async attach(data?: CustomerData): Promise<void> {
    if (!data?.id) {
      this.closeDialog.emit('cancel');
      return;
    }

    const attachData: AttachData = {
      id: data.id,
      feedbackId: this.customerDialogData().feedbackId
    };

    await this.customerModalService.attachCustomer(attachData)
      .then(() => this.attachCustomer.emit(data));
  }
}

